import React, { useState, useEffect } from 'react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import bs58 from 'bs58';
import CryptoJS from 'crypto-js';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const TransactionForm = () => {
  const { connection } = useConnection();
  const { publicKey, sendTransaction } = useWallet();
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState('');
  const [amount, setAmount] = useState('0');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [encryptionKey, setEncryptionKey] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [feesEnabled, setFeesEnabled] = useState(true);

  const MIN_AMOUNT = 0;
  const FEE_AMOUNT = 0.001;
  const FEE_WALLET = new PublicKey('A6dXNxJH3vGLJRhVFBm8vQkd2eRE2QXnYS5SXBWKLLCV');
  const MEMO_PROGRAM_ID = 'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr';
  const MESSAGE_SIGNATURE = " generated by sol messenger";

  const themes = {
    light: {
      background: '#fff',
      text: '#212529',
      secondaryText: '#6c757d',
      border: '#e9ecef',
      notice: '#f8f9fa',
      cardBg: '#f8f9fa',
      buttonBg: '#512da8',
      buttonText: 'white',
      inputBg: '#fff',
      inputBorder: '#ced4da'
    },
    dark: {
      background: '#1a1a1a',
      text: '#e9ecef',
      secondaryText: '#adb5bd',
      border: '#2d2d2d',
      notice: '#2d2d2d',
      cardBg: '#2d2d2d',
      buttonBg: '#7e57c2',
      buttonText: 'white',
      inputBg: '#333',
      inputBorder: '#4d4d4d'
    }
  };

  const toggleTheme = () => {
    setIsDarkMode(prev => {
      const newValue = !prev;
      localStorage.setItem('darkMode', JSON.stringify(newValue));
      return newValue;
    });
  };

  const encryptMessage = (message, key) => {
    if (!key) return message + MESSAGE_SIGNATURE;
    return CryptoJS.AES.encrypt(message + MESSAGE_SIGNATURE, key).toString();
  };

  const decryptMessage = (encryptedMessage, key) => {
    if (!key) return encryptedMessage;
    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedMessage, key).toString(CryptoJS.enc.Utf8);
      if (decrypted.endsWith(MESSAGE_SIGNATURE)) {
        return decrypted.slice(0, -MESSAGE_SIGNATURE.length);
      }
      return "🔒 Encrypted message (wrong key)";
    } catch {
      return "🔒 Encrypted message";
    }
  };

  useEffect(() => {
    if (publicKey) {
      fetchTransactions();
    }
  }, [publicKey, connection]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const signatures = await connection.getSignaturesForAddress(
        publicKey,
        { limit: 20 },
        'confirmed'
      );

      const txs = await Promise.all(
        signatures.map(async (sig) => {
          try {
            const tx = await connection.getParsedTransaction(sig.signature, {
              maxSupportedTransactionVersion: 0,
              commitment: 'confirmed'
            });

            let memo = '';
            let sender = '';
            let recipient = '';

            if (tx?.transaction?.message?.instructions) {
              const instructions = tx.transaction.message.instructions;
              
              // Find transfer instruction
              const transferInst = instructions.find(
                inst => inst.program === 'system' && inst.parsed?.type === 'transfer'
              );

              if (transferInst?.parsed?.info) {
                sender = transferInst.parsed.info.source;
                recipient = transferInst.parsed.info.destination;
              }

              // Find memo instruction
              const memoInst = instructions.find(
                inst => inst.programId.toString() === MEMO_PROGRAM_ID ||
                       inst.program === 'spl-memo'
              );

              if (memoInst) {
                if (memoInst.parsed) {
                  memo = memoInst.parsed;
                } else if (memoInst.data) {
                  const decodedData = bs58.decode(memoInst.data);
                  memo = new TextDecoder().decode(decodedData);
                }
              }
            }

            // Only return transactions with memos
            return memo ? {
              signature: sig.signature,
              timestamp: sig.blockTime ? new Date(sig.blockTime * 1000).toLocaleString() : 'Unknown',
              memo,
              sender,
              recipient,
              status: 'success'
            } : null;

          } catch (err) {
            console.error(`Error processing transaction ${sig.signature}:`, err);
            return null;
          }
        })
      );

      setTransactions(txs.filter(tx => tx !== null));
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (address, connectedWallet) => {
    if (address === connectedWallet?.toString()) {
      return 'Me';
    }
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value >= MIN_AMOUNT) {
      setAmount(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!publicKey || !recipient) return;

    const toastId = toast.loading("Sending transaction...");

    try {
      const recipientPubKey = new PublicKey(recipient);
      const transaction = new Transaction();

      // Add main transfer
      transaction.add(
        SystemProgram.transfer({
          fromPubkey: publicKey,
          toPubkey: recipientPubKey,
          lamports: Number(amount) * LAMPORTS_PER_SOL
        })
      );

      // Add fee transfer only if enabled
      if (feesEnabled) {
        transaction.add(
          SystemProgram.transfer({
            fromPubkey: publicKey,
            toPubkey: FEE_WALLET,
            lamports: FEE_AMOUNT * LAMPORTS_PER_SOL
          })
        );
      }

      // Add memo
      const encodedMessage = new TextEncoder().encode(encryptMessage(message, encryptionKey));
      transaction.add({
        keys: [],
        programId: new PublicKey(MEMO_PROGRAM_ID),
        data: encodedMessage,
      });

      const signature = await sendTransaction(transaction, connection);
      await connection.confirmTransaction(signature);
      
      toast.update(toastId, {
        render: "Transaction sent successfully!",
        type: "success",
        isLoading: false,
        autoClose: 5000
      });
    } catch (error) {
      console.error('Error:', error);
      toast.update(toastId, {
        render: `Transaction failed: ${error.message}`,
        type: "error",
        isLoading: false,
        autoClose: 5000
      });
    }
  };

  // Add handleAddressClick function
  const handleAddressClick = (address) => {
    setRecipient(address);
    toast.info('Address set as recipient!', {
      theme: isDarkMode ? "dark" : "light"
    });
  };

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      backgroundColor: isDarkMode ? themes.dark.background : themes.light.background,
      color: isDarkMode ? themes.dark.text : themes.light.text,
      transition: 'all 0.3s ease'
    }}>
      <button
        onClick={toggleTheme}
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          padding: '8px 16px',
          backgroundColor: 'transparent',
          border: `1px solid ${isDarkMode ? themes.dark.border : themes.light.border}`,
          borderRadius: '6px',
          color: isDarkMode ? themes.dark.text : themes.light.text,
          cursor: 'pointer'
        }}
      >
        {isDarkMode ? '☀️ Light' : '🌙 Dark'}
      </button>
      <ToastContainer theme={isDarkMode ? 'dark' : 'light'} />
      <div style={{
        backgroundColor: isDarkMode ? themes.dark.cardBg : themes.light.cardBg,
        borderRadius: '8px',
        padding: '20px',
        boxShadow: isDarkMode ? '0 2px 4px rgba(0,0,0,0.2)' : '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px',
          padding: '12px',
          backgroundColor: isDarkMode ? themes.dark.notice : themes.light.notice,
          borderRadius: '6px'
        }}>
          <div>
            <div style={{ color: isDarkMode ? themes.dark.text : themes.light.text }}>
              Platform fee: {FEE_AMOUNT} SOL
            </div>
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: '10px' 
          }}>
            <span style={{ 
              color: isDarkMode ? themes.dark.secondaryText : themes.light.secondaryText,
              fontSize: '14px'
            }}>
              Support platform
            </span>
            <label className="switch" style={{ 
              position: 'relative',
              display: 'inline-block',
              width: '60px',
              height: '34px'
            }}>
              <input
                type="checkbox"
                checked={feesEnabled}
                onChange={() => {
                  setFeesEnabled(!feesEnabled);
                  if (!feesEnabled) {
                    toast.info('Thanks for supporting the platform! 🙏', {
                      theme: isDarkMode ? "dark" : "light"
                    });
                  } else {
                    toast.info('Ok, no problem, but consider supporting the platform when you can 😊', {
                      theme: isDarkMode ? "dark" : "light"
                    });
                  }
                }}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute',
                cursor: 'pointer',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: feesEnabled ? '#512da8' : '#ccc',
                transition: '.4s',
                borderRadius: '34px',
                '&:before': {
                  position: 'absolute',
                  content: '""',
                  height: '26px',
                  width: '26px',
                  left: '4px',
                  bottom: '4px',
                  backgroundColor: 'white',
                  transition: '.4s',
                  borderRadius: '50%',
                  transform: feesEnabled ? 'translateX(26px)' : 'translateX(0)'
                }
              }}/>
            </label>
          </div>
        </div>

        <div className="fee-notice" style={{ 
          backgroundColor: isDarkMode ? themes.dark.notice : themes.light.notice,
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '20px',
          border: `1px solid ${isDarkMode ? themes.dark.border : themes.light.border}`,
          color: isDarkMode ? themes.dark.secondaryText : themes.light.secondaryText
        }}>
          Minimum transaction amount: {MIN_AMOUNT} SOL + {FEE_AMOUNT} SOL fee (if support platform is enabled, else 0 fee)
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 5px 0', color: isDarkMode ? themes.dark.text : themes.light.text }}>Recipient Address</h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: isDarkMode ? themes.dark.secondaryText : themes.light.secondaryText }}>
              Enter the Solana wallet address that will receive your message
            </p>
            <input
              type="text"
              placeholder="Recipient Address"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: `1px solid ${isDarkMode ? themes.dark.inputBorder : themes.light.inputBorder}`,
                fontSize: '16px',
                backgroundColor: isDarkMode ? themes.dark.inputBg : themes.light.inputBg,
                color: isDarkMode ? themes.dark.text : themes.light.text
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 5px 0', color: isDarkMode ? themes.dark.text : themes.light.text }}>Amount</h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: isDarkMode ? themes.dark.secondaryText : themes.light.secondaryText }}>
              Optional: Send SOL with your message (min 0 SOL + {FEE_AMOUNT} SOL fee if support platform is enabled, else 0 fee)
            </p>
            <input
              type="number"
              placeholder="Amount in SOL"
              value={amount}
              onChange={handleAmountChange}
              min={MIN_AMOUNT}
              step="0.001"
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: `1px solid ${isDarkMode ? themes.dark.inputBorder : themes.light.inputBorder}`,
                fontSize: '16px',
                backgroundColor: isDarkMode ? themes.dark.inputBg : themes.light.inputBg,
                color: isDarkMode ? themes.dark.text : themes.light.text
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 5px 0', color: isDarkMode ? themes.dark.text : themes.light.text }}>Message</h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: isDarkMode ? themes.dark.secondaryText : themes.light.secondaryText }}>
              Write your message to be stored on the Solana blockchain
            </p>
            <textarea
              placeholder="Enter your message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: `1px solid ${isDarkMode ? themes.dark.inputBorder : themes.light.inputBorder}`,
                fontSize: '16px',
                minHeight: '100px',
                resize: 'vertical',
                backgroundColor: isDarkMode ? themes.dark.inputBg : themes.light.inputBg,
                color: isDarkMode ? themes.dark.text : themes.light.text
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 5px 0', color: isDarkMode ? themes.dark.text : themes.light.text }}>Encryption Key</h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: isDarkMode ? themes.dark.secondaryText : themes.light.secondaryText }}>
              Optional: Add a secret key to encrypt your message. Share this key with the recipient to decrypt the message. Choose a strong key for better security.
            </p>
            <input
              type="password"
              placeholder="Encryption Key (optional)"
              value={encryptionKey}
              onChange={(e) => setEncryptionKey(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: `1px solid ${isDarkMode ? themes.dark.inputBorder : themes.light.inputBorder}`,
                fontSize: '16px',
                backgroundColor: isDarkMode ? themes.dark.inputBg : themes.light.inputBg,
                color: isDarkMode ? themes.dark.text : themes.light.text
              }}
            />
          </div>



          <button 
            type="submit"
            disabled={!amount || Number(amount) < MIN_AMOUNT}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: isDarkMode ? themes.dark.buttonBg : themes.light.buttonBg,
              color: isDarkMode ? themes.dark.buttonText : themes.light.buttonText,
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = isDarkMode ? '#5e35b1' : '#311b92'}
            onMouseOut={(e) => e.target.style.backgroundColor = isDarkMode ? themes.dark.buttonBg : themes.light.buttonBg}
          >
            Send Transaction
          </button>
        </form>
      </div>

      <div style={{
        backgroundColor: isDarkMode ? themes.dark.cardBg : themes.light.cardBg,
        borderRadius: '8px',
        padding: '20px',
        boxShadow: isDarkMode ? '0 2px 4px rgba(0,0,0,0.2)' : '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ 
            margin: '0',
            color: isDarkMode ? themes.dark.text : themes.light.text
          }}>Recent Messages</h3>
          
          <button 
            onClick={fetchTransactions}
            disabled={loading}
            style={{
              padding: '8px 16px',
              backgroundColor: 'transparent',
              color: isDarkMode ? themes.dark.buttonText : themes.light.buttonBg,
              border: `1px solid ${isDarkMode ? themes.dark.buttonText : themes.light.buttonBg}`,
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = isDarkMode ? themes.dark.buttonText : themes.light.buttonBg;
              e.target.style.color = isDarkMode ? themes.dark.cardBg : themes.light.buttonText;
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = isDarkMode ? themes.dark.buttonText : themes.light.buttonBg;
            }}
          >
            {loading ? 'Loading...' : '🔄 Refresh'}
          </button>
        </div>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #512da8',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }} />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : transactions.length === 0 ? (
          <p style={{ textAlign: 'center', color: isDarkMode ? themes.dark.secondaryText : themes.light.secondaryText }}>No messages found</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {transactions.map((tx) => (
              <li key={tx.signature} style={{
                padding: '15px',
                borderRadius: '6px',
                backgroundColor: isDarkMode ? themes.dark.cardBg : themes.light.cardBg,
                marginBottom: '10px',
                border: `1px solid ${isDarkMode ? themes.dark.border : themes.light.border}`,
                transition: 'transform 0.2s',
                cursor: 'pointer',
                color: isDarkMode ? themes.dark.text : themes.light.text
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
              >
                <div style={{ marginBottom: '8px', color: '#495057' }}>
                  <strong>From:</strong>{' '}
                  <span
                    onClick={() => handleAddressClick(tx.sender)}
                    style={{ 
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: isDarkMode ? '#7e57c2' : '#512da8'
                    }}
                    title="Click to set as recipient"
                  >
                    {formatAddress(tx.sender, publicKey)}
                  </span>
                </div>
                <div style={{ marginBottom: '8px', color: '#495057' }}>
                  <strong>To:</strong>{' '}
                  <span
                    onClick={() => handleAddressClick(tx.recipient)}
                    style={{ 
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: isDarkMode ? '#7e57c2' : '#512da8'
                    }}
                    title="Click to set as recipient"
                  >
                    {formatAddress(tx.recipient, publicKey)}
                  </span>
                </div>
                <div style={{ marginBottom: '8px', color: isDarkMode ? themes.dark.text : themes.light.text, fontSize: '1.1em' }}>
                  <strong>Message:</strong> 
                  <div className="message-box" style={{
                    wordBreak: 'break-all',
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                    maxWidth: '100%',
                    padding: '10px',
                    backgroundColor: isDarkMode ? themes.dark.inputBg : themes.light.inputBg,
                    borderRadius: '6px',
                    border: `1px solid ${isDarkMode ? themes.dark.inputBorder : themes.light.inputBorder}`,
                    marginTop: '5px',
                    lineHeight: '1.5',
                    color: isDarkMode ? themes.dark.text : themes.light.text
                  }}>
                    {tx.memo.endsWith(MESSAGE_SIGNATURE) 
                      ? tx.memo.slice(0, -MESSAGE_SIGNATURE.length)
                      : decryptMessage(tx.memo, encryptionKey)
                    }
                  </div>
                </div>
                <div style={{ marginBottom: '8px', color: isDarkMode ? themes.dark.secondaryText : themes.light.secondaryText, fontSize: '0.9em' }}>
                  {tx.timestamp}
                </div>
                <div style={{ fontSize: '0.8em', color: isDarkMode ? themes.dark.secondaryText : themes.light.secondaryText }}>
                  Tx: {tx.signature.slice(0, 8)}...
                </div>
              </li>
            ))}
          </ul>
        )}
        <button 
          onClick={fetchTransactions}
          disabled={loading}
          style={{
            marginTop: '15px',
            padding: '8px 16px',
            backgroundColor: 'transparent',
            color: isDarkMode ? themes.dark.buttonBg : themes.light.buttonBg,
            border: `1px solid ${isDarkMode ? themes.dark.buttonBg : themes.light.buttonBg}`,
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = isDarkMode ? themes.dark.buttonBg : themes.light.buttonBg;
            e.target.style.color = 'white';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = isDarkMode ? themes.dark.buttonBg : themes.light.buttonBg;
          }}
        >
          {loading ? 'Loading...' : 'Refresh Messages'}
        </button>
      </div>
    </div>
  );
};

export default TransactionForm;